// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-CRX-89 — editable administration protocols (premedication / anaphylaxis kit / flushing), managed from
// Drug Reference. The library read stays in `client.ts` (`listCareRxAdminProtocols`); the writes are here
// because `client.ts` is frozen for new exports (E-05). Wire shape = the C# DTOs in
// CareRxAdminProtocolWriteFunctions.cs (E-19).

import { jsonRequest as req } from './http'
import type { AdminProtocol, AdminProtocolComponent } from './client'

export type ProtocolCategory = 'premedication' | 'anaphylaxis_kit' | 'flushing'
export type ProtocolSlot = 'premed' | 'kit' | 'flush'

export const SLOT_CATEGORY: Record<ProtocolSlot, ProtocolCategory> = {
  premed: 'premedication', kit: 'anaphylaxis_kit', flush: 'flushing',
}
export const SLOT_PRINTS_TO: Record<ProtocolSlot, string> = {
  premed: 'enrollment_form_05', flush: 'enrollment_form_06', kit: 'enrollment_form_07',
}

export interface ProtocolLineInput {
  component_id: string | null
  line_text: string | null
  volume_ml: string | null
  timing: string | null
  sig: string | null
  source?: string | null
}

export interface ProtocolInput {
  name: string
  category: ProtocolCategory
  timing_minutes_prior: number | null
  prints_to: string | null
  note: string | null
  lines: ProtocolLineInput[]
}

export interface ProtocolUsageDrug { formulary_id: string; drug_reference_id: string | null; name: string }
/** What an edit / retire reaches (the drugs, i.e. NEW enrollments) and what it does not — round 2: enrolled patients
 *  keep their pinned version, so everything about them is listed as not affected. Counts only. */
export interface ProtocolUsage {
  protocol_id: string; code: string; version: number
  drugs: ProtocolUsageDrug[]
  not_affected: {
    enrolled_patients: number; unsent_forms: number; upcoming_visits: number
    sent_or_signed_forms: number; existing_pots: number; past_infusion_records: number
  }
}

export interface ProtocolWriteResult {
  id: string; code: string; version: number; copy_on_write: boolean
  repointed_drugs: number; repointed_patient_overrides: number; assigned_rows: number
}

/** Which drugs (by name) and how many patient choices use this protocol's code in this pharmacy. */
export const getProtocolUsage = (id: string) => req<ProtocolUsage>('GET', `/carerx/admin-protocols/${id}/usage`)

/** v1, this pharmacy only. `assignDrugReferenceId` = "create a new protocol for this drug instead". */
export const createProtocol = (body: ProtocolInput, assignDrugReferenceId?: string, copiedFrom?: string) =>
  req<ProtocolWriteResult>('POST', '/carerx/admin-protocols', {
    ...body,
    ...(assignDrugReferenceId ? { assign: { drug_reference_id: assignDrugReferenceId } } : {}),
    ...(copiedFrom ? { copied_from: copiedFrom } : {}),
  })

/** A new version (a global becomes this pharmacy's copy); every drug using it moves to the new version. */
export const editProtocol = (id: string, expectedVersion: number, body: ProtocolInput) =>
  req<ProtocolWriteResult>('PUT', `/carerx/admin-protocols/${id}`, { ...body, expected_version: expectedVersion })

/** active = 0, never deleted. `unassign` clears the drugs still using it first. */
export const retireProtocol = (id: string, unassign: boolean) =>
  req<ProtocolWriteResult>('POST', `/carerx/admin-protocols/${id}/retire`, { unassign })

/** The editor's starting point for an existing protocol. The list returns each line's EFFECTIVE sig (override,
 *  else the component default); a sig equal to the component default goes back as null, so a new version keeps
 *  following the catalog instead of freezing today's default into the line. */
export function toInput(p: AdminProtocol, components: AdminProtocolComponent[]): ProtocolInput {
  const byName = new Map(components.filter(c => c.comp_type === p.category).map(c => [c.name, c]))
  return {
    name: p.name,
    category: p.category as ProtocolCategory,
    timing_minutes_prior: p.timing_minutes_prior,
    prints_to: p.prints_to,
    note: p.note,
    lines: p.lines.map(l => {
      const comp = !l.is_free_text && l.item ? byName.get(l.item) : undefined
      return {
        component_id: comp?.id ?? null,
        line_text: comp ? null : l.item,
        volume_ml: l.volume_ml,
        timing: l.timing,
        sig: comp && l.sig === comp.default_sig ? null : l.sig,
        source: l.source,
      }
    }),
  }
}

/** D-CRX-89 round 2 — one protocol slot of an enrolled patient. `chosen` = staff picked a different protocol;
 *  otherwise it is the drug's protocol pinned at enrollment (maybe an older version than `latest_version`). */
export interface EnrollmentProtocolSlot {
  slot: 'premed' | 'kit' | 'flush'; protocol_id: string | null; code: string | null; name: string | null
  version: number | null; active: boolean; preset_id: string | null; chosen: boolean
  latest_id: string | null; latest_version: number | null; is_latest: boolean
  /** Pinned "no protocol": the drug had none for this slot at enrollment (protocol_id is PROTOCOL_PIN_NONE). */
  is_none?: boolean
}

/** D-CRX-89 addendum 2026-10-29: the documented "pinned: no protocol" id (server `ProtocolPin.None`). */
export const PROTOCOL_PIN_NONE = '00000000-0000-0000-0000-000000000000'

/** Assigning a protocol to an EMPTY drug slot: how many enrolled patients keep none there. */
export const getSlotImpact = (drugReferenceId: string, slot: ProtocolSlot) =>
  req<{ drug_reference_id: string; slot: ProtocolSlot; enrolled_without: number }>('GET',
    `/carerx/admin-protocols/slot-impact?drug_reference_id=${encodeURIComponent(drugReferenceId)}&slot=${slot}`)
